import { Component, Input, Output, EventEmitter, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { GeographicalRegion } from '../../../models/geographical-region.model';
import { SubGeographicalRegion } from '../../../models/sub-geographical-regions.model';
import { SubstationTemplateForSelectDataOut } from '../../../models/substation-template.model';
import { BaseFilter, CimSubstationFilter } from '../../../models/cim-substation.model';
import { AcLineSegmentFilter } from '../../../models/ac-line-segment.model';
import { PowerTransformerFilter } from '../../../models/power-transformer.model';
import { ConnectorSwitchFilter } from '../../../models/connector-switch.model';
import { AssetRating } from '../../../models/asset-rating.model';

enum NecSidemenuItems {
  RegionDistrict,
  SubstationType,
  BaseVoltage,
  Rating,
  DateTime,
  Status,
  AssetType,
  Assets,
  Losses,
  Substation,
  Feeders
}

@Component({
  selector: 'lib-nec-filter',
  templateUrl: './nec-filter.component.html',
  styleUrl: './nec-filter.component.scss',
  standalone: false
})


export class NecFilterComponent implements OnInit, OnChanges {
  @Input() regions: GeographicalRegion[] = [];
  @Input() allAreas: SubGeographicalRegion[] = [];
  @Input() substationTemplates: SubstationTemplateForSelectDataOut[] = [];
  @Input() ratings: AssetRating[] = [];
  @Input() baseVoltages: any[] = [];
  @Input() filter!: BaseFilter;
  @Output() applyFilter = new EventEmitter<void>();

  sidemenuItems: { type: NecSidemenuItems; label: string; }[] = [];

  activeItem: NecSidemenuItems = NecSidemenuItems.RegionDistrict;

  selectedAreas: { id: number; name: string }[] = [];
  selectedTemplates: { id: number; name: string }[] = [];
  selectedRatings: { id: number; name: string }[] = [];
  selectedBaseVoltages: { id: number; name: string }[] = [];
  templateSearchText: string = '';
  ratingSearchText: string = '';
  baseVoltageSearchText: string = '';

  constructor() {}

  ngOnInit(): void {
    this.updateSidemenuItems();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filter']) {
      if (this.filter?.subGeographicalRegionIds) {
        this.restoreSelectedAreas();
      }
      if (this.isAcLineFilter(this.filter)) {
        this.restoreSelectedTemplates();
      }
      if (this.isPowerTransformerFilter(this.filter)) {
        this.restoreSelectedTemplates();
        this.restoreSelectedRatings();
      }
      if (this.isConnectorSwitchFilter(this.filter)) {
        this.restoreSelectedTemplates();
        this.restoreSelectedBaseVoltages();
      }
      this.updateSidemenuItems();
    }
    if (changes['substationTemplates'] || changes['ratings'] || changes['baseVoltages']) {
      this.updateSidemenuItems();
    }
  }

  updateSidemenuItems() {
    this.sidemenuItems = [
      {type: NecSidemenuItems.RegionDistrict, label: 'Region & District'}
    ];
    
    if (this.substationTemplates && this.substationTemplates.length > 0) {
      this.sidemenuItems.push({type: NecSidemenuItems.SubstationType, label: 'Substation Type'});
    }

    if (this.filter && this.isConnectorSwitchFilter(this.filter) && this.baseVoltages && this.baseVoltages.length > 0) {
      this.sidemenuItems.push({type: NecSidemenuItems.BaseVoltage, label: 'Base Voltage'});
    }

    if (this.filter && this.isPowerTransformerFilter(this.filter) && this.ratings && this.ratings.length > 0) {
      this.sidemenuItems.push({type: NecSidemenuItems.Rating, label: 'Rating'});
    }
  }

  isAcLineFilter(filter: BaseFilter): filter is AcLineSegmentFilter {
    return 'substationTemplateIds' in filter && !('ratingIds' in filter) && !('baseVoltageIds' in filter);
  }

  isPowerTransformerFilter(filter: BaseFilter): filter is PowerTransformerFilter {
    return 'ratingIds' in filter;
  }

  isConnectorSwitchFilter(filter: BaseFilter): filter is ConnectorSwitchFilter {
    return 'baseVoltageIds' in filter;
  }

  restoreSelectedAreas() {
    if (this.filter?.subGeographicalRegionIds && this.filter.subGeographicalRegionIds.length > 0 && this.allAreas.length > 0) {
      this.selectedAreas = this.allAreas
        .filter(area => this.filter.subGeographicalRegionIds.includes(area.id!))
        .map(area => ({ id: area.id!, name: area.name }));
    }
  }

  restoreSelectedTemplates() {
    if (this.isAcLineFilter(this.filter) && this.filter.substationTemplateIds && this.filter.substationTemplateIds.length > 0 && this.substationTemplates.length > 0) {
      const acLineFilter = this.filter as AcLineSegmentFilter;
      this.selectedTemplates = this.substationTemplates
        .filter(template => acLineFilter.substationTemplateIds.includes(template.id!))
        .map(template => ({ id: template.id!, name: template.className }));
    }
    if (this.isPowerTransformerFilter(this.filter) && this.filter.substationTemplateIds && this.filter.substationTemplateIds.length > 0 && this.substationTemplates.length > 0) {
      const ptFilter = this.filter as PowerTransformerFilter;
      this.selectedTemplates = this.substationTemplates
        .filter(template => ptFilter.substationTemplateIds.includes(template.id!))
        .map(template => ({ id: template.id!, name: template.className }));
    }
    if (this.isConnectorSwitchFilter(this.filter) && this.filter.substationTemplateIds && this.filter.substationTemplateIds.length > 0 && this.substationTemplates.length > 0) {
      const csFilter = this.filter as ConnectorSwitchFilter;
      this.selectedTemplates = this.substationTemplates
        .filter(template => csFilter.substationTemplateIds.includes(template.id!))
        .map(template => ({ id: template.id!, name: template.className }));
    }
  }

  restoreSelectedRatings() {
    if (this.isPowerTransformerFilter(this.filter) && this.filter.ratingIds && this.filter.ratingIds.length > 0 && this.ratings.length > 0) {
      const ptFilter = this.filter as PowerTransformerFilter;
      this.selectedRatings = this.ratings
        .filter(rating => ptFilter.ratingIds.includes(rating.id!))
        .map(rating => ({ id: rating.id!, name: rating.kvaRating || '' }));
    }
  }

  restoreSelectedBaseVoltages() {
    if (this.isConnectorSwitchFilter(this.filter) && this.filter.baseVoltageIds && this.filter.baseVoltageIds.length > 0 && this.baseVoltages.length > 0) {
      const csFilter = this.filter as ConnectorSwitchFilter;
      this.selectedBaseVoltages = this.baseVoltages
        .filter(bv => csFilter.baseVoltageIds.includes(bv.id!))
        .map(bv => ({ id: bv.id!, name: bv.name || '' }));
    }
  }

  selectActiveItem(item: NecSidemenuItems)
  {
    this.activeItem = item;
  }

  removeArea(area: { id: number; name: string }) {
    this.selectedAreas = this.selectedAreas.filter((a) => a.id !== area.id);
  }

  removeTemplate(template: { id: number; name: string }) {
    this.selectedTemplates = this.selectedTemplates.filter((t) => t.id !== template.id);
  }

  removeRating(rating: { id: number; name: string }) {
    this.selectedRatings = this.selectedRatings.filter((r) => r.id !== rating.id);
  }

  removeBaseVoltage(bv: { id: number; name: string }) {
    this.selectedBaseVoltages = this.selectedBaseVoltages.filter((b) => b.id !== bv.id);
  }

  get filteredTemplates() {
    return this.substationTemplates.filter(
      (template) =>
        !this.templateSearchText ||
        template.className.toLowerCase().includes(this.templateSearchText.toLowerCase())
    );
  }

  get filteredRatings() {
    return this.ratings.filter(
      (rating) =>
        !this.ratingSearchText ||
        (rating.kvaRating && rating.kvaRating.toLowerCase().includes(this.ratingSearchText.toLowerCase()))
    );
  }

  get filteredBaseVoltages() {
    return this.baseVoltages.filter(
      (bv) =>
        !this.baseVoltageSearchText ||
        (bv.name && bv.name.toLowerCase().includes(this.baseVoltageSearchText.toLowerCase()))
    );
  }

  isTemplateSelected(template: { id: number; name: string }): boolean {
    return this.selectedTemplates.some((t) => t.id === template.id);
  }

  isRatingSelected(rating: { id: number; name: string }): boolean {
    return this.selectedRatings.some((r) => r.id === rating.id);
  }

  isBaseVoltageSelected(bv: { id: number; name: string }): boolean {
    return this.selectedBaseVoltages.some((b) => b.id === bv.id);
  }

  onTemplateSelect(template: { id: number; name: string }, event: Event) {
    const isChecked = (event.target as HTMLInputElement).checked;
    if (isChecked) {
      this.selectedTemplates.push(template);
    } else {
      this.selectedTemplates = this.selectedTemplates.filter((t) => t.id !== template.id);
    }
  }

  onRatingSelect(rating: { id: number; name: string }, event: Event) {
    const isChecked = (event.target as HTMLInputElement).checked;
    if (isChecked) {
      this.selectedRatings.push(rating);
    } else {
      this.selectedRatings = this.selectedRatings.filter((r) => r.id !== rating.id);
    }
  }

  onBaseVoltageSelect(bv: { id: number; name: string }, event: Event) {
    const isChecked = (event.target as HTMLInputElement).checked;
    if (isChecked) {
      this.selectedBaseVoltages.push(bv);
    } else {
      this.selectedBaseVoltages = this.selectedBaseVoltages.filter((b) => b.id !== bv.id);
    }
  }

  onApplyFilter() {
    this.filter.subGeographicalRegionIds = this.selectedAreas.map(a => a.id);
    if (this.isAcLineFilter(this.filter)) {
      (this.filter as AcLineSegmentFilter).substationTemplateIds = this.selectedTemplates.map(t => t.id);
    }
    if (this.isPowerTransformerFilter(this.filter)) {
      (this.filter as PowerTransformerFilter).substationTemplateIds = this.selectedTemplates.map(t => t.id);
      (this.filter as PowerTransformerFilter).ratingIds = this.selectedRatings.map(r => r.id);
    }
    if (this.isConnectorSwitchFilter(this.filter)) {
      (this.filter as ConnectorSwitchFilter).substationTemplateIds = this.selectedTemplates.map(t => t.id);
      (this.filter as ConnectorSwitchFilter).baseVoltageIds = this.selectedBaseVoltages.map(b => b.id);
    }
    this.applyFilter.emit();
  }

  onClearFilter() {
    this.selectedAreas = [];
    this.selectedTemplates = [];
    this.selectedRatings = [];
    this.selectedBaseVoltages = [];
    this.filter.subGeographicalRegionIds = [];
    if (this.isAcLineFilter(this.filter)) {
      (this.filter as AcLineSegmentFilter).substationTemplateIds = [];
    }
    if (this.isPowerTransformerFilter(this.filter)) {
      (this.filter as PowerTransformerFilter).substationTemplateIds = [];
      (this.filter as PowerTransformerFilter).ratingIds = [];
    }
    if (this.isConnectorSwitchFilter(this.filter)) {
      (this.filter as ConnectorSwitchFilter).substationTemplateIds = [];
      (this.filter as ConnectorSwitchFilter).baseVoltageIds = [];
    }
    this.applyFilter.emit();
  }
}
